import { useState, useEffect, useMemo, useRef } from 'react';
import { deleteOrgFile, listOrgCollection, readOrgFile, writeOrgFile } from '../../storage/FileSystemAdapter';
import RiskForm, { levelFromScore, LEVEL_COLORS } from './RiskForm';
import RiskMatrix from './RiskMatrix';
import ImportWizard from '../shared/ImportWizard';
import { BG_SUBTLE, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, BTN_DESTRUCTIVE_FILLED, BTN_SAVE_AND_LEAVE, TEXT_MUTED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, BG_SHELL, STATUS_BAD, STATUS_WARN, FONT_PAGE_TITLE, FONT_BODY, FONT_BUTTON, FONT_LABEL, FONT_SMALL, FONT_TINY } from '../../theme';
import ConfirmDialog from '../shared/ConfirmDialog';
import { resolvePerson } from '../shared/PeopleSelector';
import { dateStatus } from '../../utils/dateStatus';
import { scanReferencesToEntity } from '../../utils/intelligence';
import { appendChangeLog } from '../../utils/changeLog';
import useScrollToTopOnFormOpen from '../../utils/useScrollToTopOnFormOpen';

// ─── §11 RULE 42 — BLOCK DIALOG SUPPORT ──────────────────────────────────────
// Map a holderType from scanReferencesToEntity hits onto the App.navView
// it deep-links to.
const HOLDER_NAV = {
  'risk': 'risks', 'asset': 'assets', 'supplier': 'suppliers',
  'incident': 'incidents', 'audit': 'audits', 'nonconformity': 'nonconformities',
  'objective': 'objectives', 'metric': 'metrics', 'document': 'documents',
  'legal-register': 'legal-register', 'management-review': 'management-reviews',
  'soa-control': 'soa', 'scope': 'scope',
};

function holderLabel(t, holderType, count) {
  const key = `deleteBlockedHolder_${holderType}_${count === 1 ? 'one' : 'other'}`;
  return t[key] || holderType;
}

function summariseHits(hits, t) {
  const counts = {};
  hits.forEach(h => { counts[h.holderType] = (counts[h.holderType] || 0) + 1; });
  return Object.entries(counts).map(([type, n]) => `${n} ${holderLabel(t, type, n)}`).join(', ');
}

// ─── I18N ─────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:                 'Gestión de riesgos',
    newRisk:               '+ Nuevo riesgo',
    loading:               'Cargando riesgos…',
    emptyTitle:            'No hay riesgos registrados',
    emptyAction:           'Crear el primer riesgo',
    colRef:                'Referencia',
    colSummary:            'Descripción del riesgo',
    colOwner:              'Propietario',
    colLevel:              'Nivel',
    colTreatment:          'Tratamiento',
    colStatus:             'Estado',
    colActions:            'Acciones',
    levelLow:              'BAJO',
    levelMedium:           'MEDIO',
    levelHigh:             'ALTO',
    statusLabels: {
      'Identified':    'Identificado',
      'Being Treated': 'En tratamiento',
      'Treated':       'Tratado',
      'Accepted':      'Aceptado',
      'Closed':        'Cerrado',
    },
    treatmentOptionLabels: {
      'Modify': 'Modificar',
      'Avoid':  'Evitar',
      'Share':  'Compartir',
      'Retain': 'Retener',
    },
    viewBtn:               'Ver',
    editBtn:               'Editar',
    deleteBtn:             'Eliminar',
    deleteConfirm:         '¿Eliminar el riesgo "{name}"? Esta acción no se puede deshacer.',
    selectAll:             'Seleccionar todo',
    nSelected:             '{n} seleccionados',
    deleteSelected:        'Eliminar seleccionados',
    deleteSelectedConfirm: '¿Eliminar los siguientes riesgos?\n\n{names}\n\nEsta acción no se puede deshacer.',
    saveError:             'Error al guardar. Inténtalo de nuevo.',
    importCsv:             'Importar CSV',
    importRisksTitle:      'Importar riesgos',
    filterRisk:            'Riesgo',
    filterRiskPh:          'Buscar…',
    filterLevel:           'Nivel',
    filterOwner:           'Responsable',
    filterTreatment:       'Tratamiento',
    filterStatus:          'Estado',
    filterAll:             'Todos',
    formCancel:            'Cancelar',
    unsavedLabel:          'Cambios sin guardar',
    unsavedConfirm:        '¿Salir sin guardar los cambios?',
    leaveWithoutSaving:    'Salir sin guardar',
    saveAndLeave:          'Guardar y salir',
    badgeOverdue:          'Vencido',
    badgeSoon:             'Vence pronto',
    deleteBlockedTitle:        'No se puede eliminar: existen referencias',
    deleteBlockedReferencedBy: 'Referenciado por',
    deleteBlockedOpenHolder:   'Abrir',
    deactivateInstead:         'Desactivar en su lugar',
    bulkDeleteSummary_allBlocked: 'Ninguno de los registros seleccionados puede eliminarse porque todos están referenciados por otros registros.',
    bulkDeleteSummary_mixed:      '{deletable} de {total} registros pueden eliminarse; {blocked} están bloqueados por referencias.',
    continueWithDeletables:       'Eliminar los registros no bloqueados',
    deleteBlockedHolder_risk_one:               'riesgo',
    deleteBlockedHolder_risk_other:             'riesgos',
    deleteBlockedHolder_asset_one:              'activo',
    deleteBlockedHolder_asset_other:            'activos',
    deleteBlockedHolder_supplier_one:           'proveedor',
    deleteBlockedHolder_supplier_other:         'proveedores',
    deleteBlockedHolder_incident_one:           'incidente',
    deleteBlockedHolder_incident_other:         'incidentes',
    deleteBlockedHolder_audit_one:              'auditoría',
    deleteBlockedHolder_audit_other:            'auditorías',
    deleteBlockedHolder_nonconformity_one:      'no conformidad',
    deleteBlockedHolder_nonconformity_other:    'no conformidades',
    deleteBlockedHolder_objective_one:          'objetivo',
    deleteBlockedHolder_objective_other:        'objetivos',
    deleteBlockedHolder_metric_one:             'métrica',
    deleteBlockedHolder_metric_other:           'métricas',
    deleteBlockedHolder_document_one:           'documento',
    deleteBlockedHolder_document_other:         'documentos',
    'deleteBlockedHolder_legal-register_one':   'entrada del registro legal',
    'deleteBlockedHolder_legal-register_other': 'entradas del registro legal',
    'deleteBlockedHolder_management-review_one':   'revisión por la dirección',
    'deleteBlockedHolder_management-review_other': 'revisiones por la dirección',
    'deleteBlockedHolder_soa-control_one':      'control del SoA',
    'deleteBlockedHolder_soa-control_other':    'controles del SoA',
    deleteBlockedHolder_scope_one:              'alcance',
    deleteBlockedHolder_scope_other:            'alcance',
  },
  en: {
    title:                 'Risk management',
    newRisk:               '+ New risk',
    loading:               'Loading risks…',
    emptyTitle:            'No risks recorded yet',
    emptyAction:           'Create your first risk',
    colRef:                'Reference',
    colSummary:            'Risk summary',
    colOwner:              'Owner',
    colLevel:              'Level',
    colTreatment:          'Treatment',
    colStatus:             'Status',
    colActions:            'Actions',
    levelLow:              'LOW',
    levelMedium:           'MEDIUM',
    levelHigh:             'HIGH',
    statusLabels: {
      'Identified':    'Identified',
      'Being Treated': 'Being Treated',
      'Treated':       'Treated',
      'Accepted':      'Accepted',
      'Closed':        'Closed',
    },
    treatmentOptionLabels: {
      'Modify': 'Modify',
      'Avoid':  'Avoid',
      'Share':  'Share',
      'Retain': 'Retain',
    },
    viewBtn:               'View',
    editBtn:               'Edit',
    deleteBtn:             'Delete',
    deleteConfirm:         'Delete risk "{name}"? This cannot be undone.',
    selectAll:             'Select all',
    nSelected:             '{n} selected',
    deleteSelected:        'Delete selected',
    deleteSelectedConfirm: 'Delete the following risks?\n\n{names}\n\nThis cannot be undone.',
    saveError:             'Could not save. Please try again.',
    importCsv:             'Import CSV',
    importRisksTitle:      'Import risks',
    filterRisk:            'Risk',
    filterRiskPh:          'Search…',
    filterLevel:           'Level',
    filterOwner:           'Owner',
    filterTreatment:       'Treatment',
    filterStatus:          'Status',
    filterAll:             'All',
    formCancel:            'Cancel',
    unsavedLabel:          'Unsaved changes',
    unsavedConfirm:        'Leave without saving changes?',
    leaveWithoutSaving:    'Leave without saving',
    saveAndLeave:          'Save and leave',
    badgeOverdue:          'Overdue',
    badgeSoon:             'Due soon',
    deleteBlockedTitle:        'Cannot delete: references found',
    deleteBlockedReferencedBy: 'Referenced by',
    deleteBlockedOpenHolder:   'Open',
    deactivateInstead:         'Deactivate instead',
    bulkDeleteSummary_allBlocked: 'None of the selected records can be deleted because all of them are referenced by other records.',
    bulkDeleteSummary_mixed:      '{deletable} of {total} records can be deleted; {blocked} are blocked by references.',
    continueWithDeletables:       'Delete the unblocked records',
    deleteBlockedHolder_risk_one:               'risk',
    deleteBlockedHolder_risk_other:             'risks',
    deleteBlockedHolder_asset_one:              'asset',
    deleteBlockedHolder_asset_other:            'assets',
    deleteBlockedHolder_supplier_one:           'supplier',
    deleteBlockedHolder_supplier_other:         'suppliers',
    deleteBlockedHolder_incident_one:           'incident',
    deleteBlockedHolder_incident_other:         'incidents',
    deleteBlockedHolder_audit_one:              'audit',
    deleteBlockedHolder_audit_other:            'audits',
    deleteBlockedHolder_nonconformity_one:      'nonconformity',
    deleteBlockedHolder_nonconformity_other:    'nonconformities',
    deleteBlockedHolder_objective_one:          'objective',
    deleteBlockedHolder_objective_other:        'objectives',
    deleteBlockedHolder_metric_one:             'metric',
    deleteBlockedHolder_metric_other:           'metrics',
    deleteBlockedHolder_document_one:           'document',
    deleteBlockedHolder_document_other:         'documents',
    'deleteBlockedHolder_legal-register_one':   'legal register entry',
    'deleteBlockedHolder_legal-register_other': 'legal register entries',
    'deleteBlockedHolder_management-review_one':   'management review',
    'deleteBlockedHolder_management-review_other': 'management reviews',
    'deleteBlockedHolder_soa-control_one':      'SoA control',
    'deleteBlockedHolder_soa-control_other':    'SoA controls',
    deleteBlockedHolder_scope_one:              'scope',
    deleteBlockedHolder_scope_other:            'scope',
  },
};

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const TREATMENT_OPTIONS = ['Modify', 'Avoid', 'Share', 'Retain'];
const STATUS_OPTIONS    = ['Identified', 'Being Treated', 'Treated', 'Accepted', 'Closed'];
const LEVEL_OPTIONS     = ['LOW', 'MEDIUM', 'HIGH'];

// ─── LEVEL BADGE ──────────────────────────────────────────────────────────────
// Reads the persisted uppercase level from the Risk record per §11 rule 5.
// Never re-derives from score at render.
function LevelBadge({ level, t }) {
  const colors = LEVEL_COLORS[level] || LEVEL_COLORS.LOW;
  const label = level === 'LOW' ? t.levelLow : level === 'MEDIUM' ? t.levelMedium : t.levelHigh;
  return (
    <span style={{
      ...FONT_SMALL, fontWeight: 600,
      background: colors.bg, color: colors.text,
      borderRadius: 5, padding: '0.2rem 0.5rem',
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function RiskList({ lang, org, onBack, onNavigate, onDirtyChange, saveRequestTick: appSaveRequestTick, onSaveResult, initialEditId, initialViewId }) {
  const t = T[lang] || T.en;
  const [risks, setRisks] = useState(null);          // null = loading
  const [formMode, setFormMode] = useState(null);    // null | 'create' | 'edit'
  const [editRisk, setEditRisk] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [listError, setListError]     = useState('');
  const [showImport, setShowImport]   = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [dirty, setDirty]               = useState(false);
  const [saveRequestTick, setSaveRequestTick] = useState(0);
  const [scanningDelete, setScanningDelete] = useState(false);
  // App-level "Save and leave" — flagged when the App tick increases so
  // the next successful form save reports back via onSaveResult instead
  // of running the form-Cancel close-form path. See GRC_UI_PATTERNS.md §5.
  const pendingAppSaveRef               = useRef(false);
  const lastProcessedAppTickRef         = useRef(0);
  const [people, setPeople] = useState([]);

  // Filters
  const [filterText,      setFilterText]      = useState('');
  const [filterLevel,     setFilterLevel]     = useState('');
  const [filterOwner,     setFilterOwner]     = useState('');
  const [filterTreatment, setFilterTreatment] = useState('');
  const [filterStatus,    setFilterStatus]    = useState('');

  useScrollToTopOnFormOpen(formMode !== null);

  const showError = (msg) => { setListError(msg); setTimeout(() => setListError(''), 4000); };

  const loadRisks = () => {
    listOrgCollection(org.id, 'risks').then((items) => {
      const sorted = [...items].sort((a, b) => {
        if (a.ref && b.ref) return a.ref.localeCompare(b.ref);
        return 0;
      });
      setRisks(sorted);
    }).catch(() => setRisks([]));
  };

  useEffect(() => {
    loadRisks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id]);

  useEffect(() => {
    readOrgFile(org.id, 'people.json')
      .then(data => setPeople(Array.isArray(data.people) ? data.people : []))
      .catch(() => setPeople([]));
  }, [org.id]);

  // Deep-link from Dashboard Risk gaps panel — open the target risk in edit
  // mode once the list has loaded. Fires once per incoming initialEditId.
  useEffect(() => {
    if (!initialEditId || !Array.isArray(risks)) return;
    const target = risks.find(r => r.id === initialEditId);
    if (target) {
      setDirty(false);
      setEditRisk(target);
      setFormMode('edit');
    }
  }, [initialEditId, risks]);

  // Deep-link from RTPView — open the target risk in view mode once the list
  // has loaded. Fires once per incoming initialViewId.
  useEffect(() => {
    if (!initialViewId || !Array.isArray(risks)) return;
    const target = risks.find(r => r.id === initialViewId);
    if (target) {
      setDirty(false);
      setEditRisk(target);
      setFormMode('view');
    }
  }, [initialViewId, risks]);

  const handleFormSave = (fileId) => {
    if (fileId === null) {
      setFormMode(null);
      setEditRisk(null);
      setDirty(false);
      setSaveRequestTick(0);
      loadRisks();
      return;
    }
    setDirty(false);
    setSaveRequestTick(0);
    loadRisks();
    if (pendingAppSaveRef.current) {
      pendingAppSaveRef.current = false;
      onSaveResult?.(true);
    }
  };

  const closeForm = () => {
    setFormMode(null);
    setEditRisk(null);
    setDirty(false);
  };

  const handleFormCancel = () => {
    if (dirty) {
      setConfirmState({
        message: t.unsavedConfirm,
        confirmLabel: t.leaveWithoutSaving,
        onConfirm: () => { setConfirmState(null); closeForm(); },
        secondaryAction: {
          label: t.saveAndLeave,
          color: BTN_SAVE_AND_LEAVE,
          onClick: () => {
            setConfirmState(null);
            setSaveRequestTick(n => n + 1);
          },
        },
      });
      return;
    }
    closeForm();
  };

  // ── App-level guard: bridge App tick into local tick to fire the embedded
  //    form's existing saveRequestTick handler. See GRC_UI_PATTERNS.md §5.
  useEffect(() => {
    if (typeof appSaveRequestTick !== 'number') return;
    if (appSaveRequestTick <= lastProcessedAppTickRef.current) return;
    lastProcessedAppTickRef.current = appSaveRequestTick;
    if (formMode) {
      pendingAppSaveRef.current = true;
      setSaveRequestTick(n => n + 1);
    } else {
      // No form open — nothing to save; report success so App can navigate.
      onSaveResult?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSaveRequestTick]);

  // ── beforeunload browser guard ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  const openEdit = (risk) => {
    setDirty(false);
    setEditRisk(risk);
    setFormMode('edit');
  };

  const openView = (risk) => {
    setDirty(false);
    setEditRisk(risk);
    setFormMode('view');
  };

  const openCreate = () => {
    setDirty(false);
    setEditRisk(null);
    setFormMode('create');
  };

  const allRisks = useMemo(() => (Array.isArray(risks) ? risks : []), [risks]);

  const ownerOptions = useMemo(() => {
    const set = new Set();
    allRisks.forEach(r => { if (r.owner) set.add(r.owner); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allRisks]);

  const safeRisks = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    return allRisks.filter(r => {
      if (q) {
        const hay = (r.riskSummary || r.title || '').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filterLevel) {
        // Use the persisted level per §11 rule 5 — fall back to the canonical
        // unified function only when records predate riskLevel persistence.
        const persisted = r.riskLevel;
        const level = persisted || levelFromScore(
          r.inherentScore || (r.likelihood * r.impact) || 0,
          org.riskMatrix && org.riskMatrix.thresholds
        );
        if (level !== filterLevel) return false;
      }
      if (filterOwner     && r.owner           !== filterOwner)     return false;
      if (filterTreatment && r.treatmentOption !== filterTreatment) return false;
      if (filterStatus    && r.status          !== filterStatus)    return false;
      return true;
    });
  }, [allRisks, filterText, filterLevel, filterOwner, filterTreatment, filterStatus, org.riskMatrix]);

  const selectedInView = safeRisks.filter((r) => selectedIds.has(r.id));
  const allChecked     = safeRisks.length > 0 && selectedInView.length === safeRisks.length;
  const someChecked    = selectedInView.length > 0;

  const toggleAll = (checked) => {
    if (checked) setSelectedIds(new Set(safeRisks.map((r) => r.id)));
    else         setSelectedIds(new Set());
  };
  const toggleOne = (id, checked) => {
    setSelectedIds((prev) => { const next = new Set(prev); if (checked) next.add(id); else next.delete(id); return next; });
  };

  // §11 rule 42 — Risk deactivation: status="Closed".
  const handleDeactivateRisk = async (risk) => {
    try {
      const existing = await readOrgFile(org.id, `risks/${risk.id}.json`).catch(() => risk);
      const patch = { status: 'Closed' };
      const merged = { ...existing, ...patch, updatedAt: new Date().toISOString() };
      merged.changeLog = appendChangeLog(existing, patch, org.consultantPersonId || 'system');
      await writeOrgFile(org.id, `risks/${risk.id}.json`, merged);
      loadRisks();
    } catch { showError(t.saveError); }
  };

  const renderBlockContent = (risk, hits) => {
    const showDeactivate = risk.status !== 'Closed';
    return (
      <div>
        <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
          {t.deleteBlockedTitle}
        </h3>
        <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
          {t.deleteBlockedReferencedBy} {summariseHits(hits, t)}
        </p>
        <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
          {hits.map((h, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { setConfirmState(null); onNavigate?.(HOLDER_NAV[h.holderType], null); }}
              style={{ ...BTN_SECONDARY, ...FONT_LABEL, borderRadius: 6, padding: '0.35rem 0.6rem', textAlign: 'left', cursor: 'pointer' }}
            >
              {`${holderLabel(t, h.holderType, 1)} ${h.holderRef}`}
            </button>
          ))}
        </div>
        {!showDeactivate ? null : (
          <div style={{ ...FONT_LABEL, color: TEXT_MUTED, margin: 0 }} />
        )}
      </div>
    );
  };

  const handleDeleteOne = async (risk) => {
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'risk', risk.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      const name = risk.riskSummary || risk.title || risk.ref || risk.id;
      setConfirmState({
        message: t.deleteConfirm.replace('{name}', name),
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `risks/${risk.id}.json`);
            setSelectedIds((prev) => { const next = new Set(prev); next.delete(risk.id); return next; });
            loadRisks();
          } catch { showError(t.saveError); }
        },
      });
      return;
    }
    const showDeactivate = risk.status !== 'Closed';
    setConfirmState({
      content: renderBlockContent(risk, hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
      secondaryAction: showDeactivate ? {
        label: t.deactivateInstead,
        color: BTN_DESTRUCTIVE_OUTLINE,
        onClick: () => { setConfirmState(null); handleDeactivateRisk(risk); },
      } : undefined,
    });
  };

  const renderBulkContent = (allBlocked, deletableCount, blockedCount, total, blockedSummaries) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {allBlocked
          ? t.bulkDeleteSummary_allBlocked
          : t.bulkDeleteSummary_mixed
              .replace('{deletable}', String(deletableCount))
              .replace('{blocked}',   String(blockedCount))
              .replace('{total}',     String(total))}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {blockedSummaries.map((row, i) => (
          <div key={i} style={{ ...FONT_LABEL, color: TEXT_DEFAULT, padding: '0.3rem 0.5rem', border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, background: BG_SURFACE }}>
            <span style={{ fontWeight: 600 }}>{row.label}</span>
            <span style={{ color: TEXT_MUTED }}>{` — ${t.deleteBlockedReferencedBy} ${row.summary}`}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const handleDeleteSelected = async () => {
    if (!selectedInView.length) return;
    if (scanningDelete) return;
    setScanningDelete(true);
    const items = [...selectedInView];
    let hitsLists;
    try {
      hitsLists = await Promise.all(items.map(r => scanReferencesToEntity(org.id, 'risk', r.id)));
    } catch {
      hitsLists = items.map(() => []);
    }
    setScanningDelete(false);
    const deletable = [];
    const blocked   = [];
    items.forEach((r, i) => {
      if ((hitsLists[i] || []).length === 0) deletable.push(r);
      else blocked.push({ record: r, hits: hitsLists[i] });
    });
    const performBulkDelete = async (cohort) => {
      await Promise.all(cohort.map((r) => deleteOrgFile(org.id, `risks/${r.id}.json`).catch(() => {})));
      setSelectedIds(new Set());
      loadRisks();
    };
    if (blocked.length === 0) {
      const names = items.map((r) => r.riskSummary || r.title || r.ref || r.id).join(', ');
      setConfirmState({
        message: t.deleteSelectedConfirm.replace('{names}', names),
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          await performBulkDelete(items);
        },
      });
      return;
    }
    const blockedSummaries = blocked.map(({ record, hits }) => ({
      label: record.riskSummary || record.title || record.ref || record.id,
      summary: summariseHits(hits, t),
    }));
    const allBlocked = deletable.length === 0;
    setConfirmState({
      content: renderBulkContent(allBlocked, deletable.length, blocked.length, items.length, blockedSummaries),
      confirmLabel: allBlocked ? t.formCancel : t.continueWithDeletables,
      confirmColor: allBlocked ? BTN_SECONDARY : BTN_DESTRUCTIVE_FILLED,
      onConfirm: async () => {
        setConfirmState(null);
        if (allBlocked) return;
        await performBulkDelete(deletable);
      },
    });
  };

  // ── Import view ───────────────────────────────────────────────────────────
  if (showImport) {
    return (
      <ImportWizard
        lang={lang}
        dedupeKey="ref"
        onClose={() => setShowImport(false)}
        onImport={async (rows) => {
          const existing = await listOrgCollection(org.id, 'risks').catch(() => []);
          const year = new Date().getFullYear();
          const day = new Date().toISOString().slice(0, 10).replace(/-/g, '');
          const now = new Date().toISOString();

          // Track max sequences across existing + batch
          const refPrefix = `RISK-${year}-`;
          let maxRefSeq = 0;
          existing.forEach(r => {
            if (r.ref && r.ref.startsWith(refPrefix)) {
              const n = parseInt(r.ref.slice(refPrefix.length), 10);
              if (!isNaN(n) && n > maxRefSeq) maxRefSeq = n;
            }
          });

          const dayPrefix = `risk-${day}-`;
          let maxFileSeq = 0;
          existing.forEach(r => {
            if (r.id && r.id.startsWith(dayPrefix)) {
              const n = parseInt(r.id.slice(dayPrefix.length), 10);
              if (!isNaN(n) && n > maxFileSeq) maxFileSeq = n;
            }
          });

          let importedCount = 0;
          for (const row of rows) {
            // Skip if ref already exists
            if (row.ref) {
              const dup = existing.find(r => r.ref === row.ref);
              if (dup) continue;
            }
            const ref = row.ref || `${refPrefix}${String(++maxRefSeq).padStart(3, '0')}`;
            if (!row.ref) {
              // also count for next iteration
            } else {
              // Update maxRefSeq if needed
              if (ref.startsWith(refPrefix)) {
                const n = parseInt(ref.slice(refPrefix.length), 10);
                if (!isNaN(n) && n > maxRefSeq) maxRefSeq = n;
              }
            }
            const fileId = `${dayPrefix}${String(++maxFileSeq).padStart(3, '0')}`;
            const likelihood = row.likelihood || 3;
            const impact = row.impact || 3;
            const inherentScore = likelihood * impact;
            const riskLevelStr = levelFromScore(inherentScore, org.riskMatrix && org.riskMatrix.thresholds);
            const riskType = Array.isArray(row.riskType) ? row.riskType : [];

            const riskObj = {
              id: fileId, orgId: org.id, ref,
              riskSummary: row.riskSummary || '', description: row.description || '',
              riskType,
              identifiedDate: null, identifiedBy: null,
              category: row.category || 'Other',
              owner: row.owner || '',
              existingControls: row.existingControls || '',
              likelihood, likelihoodRationale: row.likelihoodRationale || '',
              impact, impactRationale: row.impactRationale || '',
              inherentScore, riskLevel: riskLevelStr,
              treatmentOption: '', proposedTreatmentAction: '',
              controlReferences: [],
              treatmentActionOwner: [],
              treatmentApprovedBy: null, treatmentApprovalDate: null,
              treatmentDeadline: null,
              treatmentActionProgress: '', treatmentActionStatus: 'Not Started',
              linkedControlIds: [],
              residualLikelihood: null, residualImpact: null,
              residualScore: null, residualRiskLevel: null,
              acceptanceCriteria: '', accepted: false,
              acceptedBy: null, acceptanceDate: null,
              status: 'Identified',
              nextReviewDate: null, residualRiskAccepted: false,
              interviewNotes: null,
              linkedAssetIds: [], linkedSupplierIds: [],
              linkedIncidentIds: [], linkedNonconformityIds: [],
              changeLog: [],
              createdAt: now, updatedAt: now,
            };
            await writeOrgFile(org.id, `risks/${fileId}.json`, riskObj);
            importedCount++;
          }
          loadRisks();
          return importedCount;
        }}
        columns={[
          { key: 'ref',                label: 'Reference',           required: false, type: 'string',  default: null },
          { key: 'category',           label: 'Category',            required: false, type: 'string',  default: 'Other' },
          { key: 'riskSummary',        label: 'Risk Summary',        required: true,  type: 'string' },
          { key: 'description',        label: 'Description',         required: false, type: 'string',  default: '' },
          { key: 'owner',              label: 'Owner',               required: true,  type: 'string' },
          { key: 'riskType',           label: 'Risk Type (CIA)',      required: false, type: 'array',   default: [] },
          { key: 'existingControls',   label: 'Existing Controls',   required: false, type: 'string',  default: '' },
          { key: 'likelihood',         label: 'Likelihood',          required: true,  type: 'number',  enum: [1,2,3,4,5] },
          { key: 'likelihoodRationale',label: 'Likelihood Rationale',required: false, type: 'string',  default: '' },
          { key: 'impact',             label: 'Impact',              required: true,  type: 'number',  enum: [1,2,3,4,5] },
          { key: 'impactRationale',    label: 'Impact Rationale',    required: false, type: 'string',  default: '' },
        ]}
        templateFilename="import-risks.csv"
        title={{ es: t.importRisksTitle, en: 'Import risks' }}
      />
    );
  }

  // ── Form view ─────────────────────────────────────────────────────────────
  if (formMode) {
    return (
      <>
        <RiskForm
          mode={formMode}
          risk={editRisk}
          org={org}
          lang={lang}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
          onFormChange={() => setDirty(true)}
          saveRequestTick={saveRequestTick}
          dirty={dirty}
        />
        <ConfirmDialog
          open={confirmState !== null}
          message={confirmState?.message}
          content={confirmState?.content}
          onConfirm={confirmState?.onConfirm}
          onCancel={() => setConfirmState(null)}
          confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
          cancelLabel={t.formCancel}
          confirmColor={confirmState?.confirmColor}
          secondaryAction={confirmState?.secondaryAction}
        />
      </>
    );
  }

  // ── Loading short-circuit ─────────────────────────────────────────────────
  if (risks === null) {
    return (
      <div>
        {/* Header row */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}>
          <div>
            <h1 style={{
              ...FONT_PAGE_TITLE, color: TEXT_HEADING, margin: 0,
            }}>
              {t.title}
            </h1>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setShowImport(true)}
              style={{
                ...BTN_SECONDARY,
                ...FONT_BUTTON,
                borderRadius: 8, padding: '0.55rem 1.25rem', cursor: 'pointer',
              }}
            >
              {t.importCsv}
            </button>
            <button
              onClick={openCreate}
              style={{
                ...BTN_PRIMARY,
                ...FONT_BUTTON,
                border: 'none',
                borderRadius: 8, padding: '0.55rem 1.25rem', cursor: 'pointer',
              }}
            >
              {t.newRisk}
            </button>
          </div>
        </div>
        <p style={{ ...FONT_LABEL, color: TEXT_MUTED }}>
          {t.loading}
        </p>
        <ConfirmDialog
          open={confirmState !== null}
          message={confirmState?.message}
          content={confirmState?.content}
          onConfirm={confirmState?.onConfirm}
          onCancel={() => setConfirmState(null)}
          confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
          cancelLabel={t.formCancel}
          confirmColor={confirmState?.confirmColor}
          secondaryAction={confirmState?.secondaryAction}
        />
      </div>
    );
  }

  // ── Table header style ────────────────────────────────────────────────────
  const thStyle = {
    ...FONT_TINY,
    color: TEXT_MUTED,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    padding: '0.6rem 0.75rem',
    textAlign: 'left',
    borderBottom: `1px solid ${BORDER_DEFAULT}`,
    whiteSpace: 'nowrap',
    background: BG_SUBTLE,
  };

  const tdStyle = {
    ...FONT_BODY,
    color: TEXT_DEFAULT,
    padding: '0.7rem 0.75rem',
    borderBottom: `1px solid ${BG_SHELL}`,
    verticalAlign: 'middle',
  };

  const btnSmall = {
    ...BTN_SECONDARY,
    ...FONT_TINY,
    borderRadius: 5,
    padding: '0.2rem 0.5rem', cursor: 'pointer',
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header row */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '0.75rem',
      }}>
        <div>
          <h1 style={{
            ...FONT_PAGE_TITLE, color: TEXT_HEADING, margin: 0,
          }}>
            {t.title}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => setShowImport(true)}
            style={{
              ...BTN_SECONDARY,
              ...FONT_BUTTON,
              borderRadius: 8, padding: '0.55rem 1.25rem', cursor: 'pointer',
            }}
          >
            {t.importCsv}
          </button>
          <button
            onClick={openCreate}
            style={{
              ...BTN_PRIMARY,
              ...FONT_BUTTON,
              border: 'none',
              borderRadius: 8, padding: '0.55rem 1.25rem', cursor: 'pointer',
            }}
          >
            {t.newRisk}
          </button>
        </div>
      </div>

      <RiskMatrix lang={lang} thresholds={org.riskMatrix && org.riskMatrix.thresholds} />

      {listError && (
        <div style={{ ...FONT_LABEL, marginBottom: '0.75rem', padding: '0.6rem 1rem', background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`, borderRadius: 8, color: ERROR_TEXT }}>
          {listError}
        </div>
      )}

      {/* Bulk action bar */}
      {someChecked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', marginBottom: '0.6rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
          <span style={{ ...FONT_LABEL, color: BRAND_PRIMARY_DARK }}>
            {t.nSelected.replace('{n}', selectedInView.length)}
          </span>
          <button onClick={handleDeleteSelected} style={{ ...btnSmall, border: BTN_DESTRUCTIVE_OUTLINE.border, color: BTN_DESTRUCTIVE_OUTLINE.color }}>{t.deleteSelected}</button>
        </div>
      )}

      {/* Loading */}
      {risks === null && (
        <p style={{ ...FONT_LABEL, color: TEXT_MUTED }}>
          {t.loading}
        </p>
      )}

      {/* Filter bar */}
      {risks !== null && allRisks.length > 0 && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterRisk}</span>
            <input type="text" value={filterText} onChange={e => setFilterText(e.target.value)} placeholder={t.filterRiskPh}
              style={{ ...FONT_LABEL, background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.3rem 0.5rem', color: TEXT_DEFAULT, minWidth: 180 }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterLevel}</span>
            <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)}
              style={{ ...FONT_LABEL, background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.3rem 0.5rem', color: TEXT_DEFAULT }}>
              <option value="">{t.filterAll}</option>
              {LEVEL_OPTIONS.map(l => (
                <option key={l} value={l}>{l === 'LOW' ? t.levelLow : l === 'MEDIUM' ? t.levelMedium : t.levelHigh}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterOwner}</span>
            <select value={filterOwner} onChange={e => setFilterOwner(e.target.value)}
              style={{ ...FONT_LABEL, background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.3rem 0.5rem', color: TEXT_DEFAULT }}>
              <option value="">{t.filterAll}</option>
              {ownerOptions.map(o => (<option key={o} value={o}>{resolvePerson(o, people)}</option>))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterTreatment}</span>
            <select value={filterTreatment} onChange={e => setFilterTreatment(e.target.value)}
              style={{ ...FONT_LABEL, background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.3rem 0.5rem', color: TEXT_DEFAULT }}>
              <option value="">{t.filterAll}</option>
              {TREATMENT_OPTIONS.map(o => (<option key={o} value={o}>{t.treatmentOptionLabels[o] || o}</option>))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterStatus}</span>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              style={{ ...FONT_LABEL, background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.3rem 0.5rem', color: TEXT_DEFAULT }}>
              <option value="">{t.filterAll}</option>
              {STATUS_OPTIONS.map(o => (<option key={o} value={o}>{t.statusLabels[o] || o}</option>))}
            </select>
          </div>
        </div>
      )}

      {/* Empty state */}
      {risks !== null && risks.length === 0 && (
        <div style={{
          background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10,
          padding: '3rem', textAlign: 'center',
        }}>
          <p style={{
            ...FONT_BODY,
            color: TEXT_MUTED, marginBottom: '1.25rem',
          }}>
            {t.emptyTitle}
          </p>
          <button
            onClick={openCreate}
            style={{
              ...BTN_PRIMARY,
              ...FONT_BUTTON,
              border: 'none',
              borderRadius: 8, padding: '0.65rem 1.5rem', cursor: 'pointer',
            }}
          >
            {t.emptyAction}
          </button>
        </div>
      )}

      {/* Table */}
      {risks !== null && risks.length > 0 && (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${BORDER_DEFAULT}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: BG_SURFACE }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 36, padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    title={t.selectAll}
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </th>
                <th style={thStyle}>{t.colRef}</th>
                <th style={{ ...thStyle, minWidth: 180 }}>{t.colSummary}</th>
                <th style={thStyle}>{t.colLevel}</th>
                <th style={thStyle}>{t.colOwner}</th>
                <th style={thStyle}>{t.colTreatment}</th>
                <th style={thStyle}>{t.colStatus}</th>
                <th style={thStyle}>{t.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {safeRisks.map((risk, idx) => {
                const score = risk.inherentScore || (risk.likelihood * risk.impact) || 0;
                // Read persisted level per §11 rule 5; fall back to canonical
                // unified function only for pre-migration records.
                const level = risk.riskLevel || levelFromScore(
                  score,
                  org.riskMatrix && org.riskMatrix.thresholds
                );
                return (
                  <tr key={risk.id} style={{ background: idx % 2 === 0 ? BG_SURFACE : 'transparent' }}>
                    <td style={{ ...tdStyle, textAlign: 'center', padding: '0.55rem 0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(risk.id)}
                        onChange={(e) => toggleOne(risk.id, e.target.checked)}
                      />
                    </td>
                    <td style={{ ...tdStyle, ...FONT_LABEL, color: TEXT_MUTED, whiteSpace: 'nowrap' }}>
                      {risk.ref || risk.id}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: TEXT_HEADING }}>
                      {risk.riskSummary || risk.title || '—'}
                    </td>
                    <td style={tdStyle}>
                      <LevelBadge level={level} t={t} />
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      {resolvePerson(risk.owner, people) || '—'}
                    </td>
                    <td style={{ ...tdStyle, ...FONT_LABEL }}>
                      {risk.treatmentOption ? (t.treatmentOptionLabels[risk.treatmentOption] || risk.treatmentOption) : '—'}
                    </td>
                    <td style={{ ...tdStyle, ...FONT_LABEL }}>
                      {(() => {
                        const treatmentBadge = (risk.status === 'Closed' || risk.treatmentActionStatus === 'Completed' || risk.treatmentOption === 'Retain')
                          ? null
                          : dateStatus(risk.treatmentDeadline);
                        const reviewBadge = risk.status === 'Closed'
                          ? null
                          : dateStatus(risk.nextReviewDate);
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                            <span>{risk.status ? (t.statusLabels[risk.status] || risk.status) : '—'}</span>
                            {treatmentBadge && (
                              <span style={{
                                ...FONT_TINY,
                                fontWeight: 600,
                                background: treatmentBadge === 'overdue' ? STATUS_BAD.bg : STATUS_WARN.bg,
                                color:      treatmentBadge === 'overdue' ? STATUS_BAD.text : STATUS_WARN.text,
                                borderRadius: 4,
                                padding: '0.15rem 0.45rem',
                                whiteSpace: 'nowrap',
                              }}>
                                {treatmentBadge === 'overdue' ? t.badgeOverdue : t.badgeSoon}
                              </span>
                            )}
                            {reviewBadge && (
                              <span style={{
                                ...FONT_TINY,
                                fontWeight: 600,
                                background: reviewBadge === 'overdue' ? STATUS_BAD.bg : STATUS_WARN.bg,
                                color:      reviewBadge === 'overdue' ? STATUS_BAD.text : STATUS_WARN.text,
                                borderRadius: 4,
                                padding: '0.15rem 0.45rem',
                                whiteSpace: 'nowrap',
                              }}>
                                {reviewBadge === 'overdue' ? t.badgeOverdue : t.badgeSoon}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button onClick={() => openView(risk)} style={btnSmall}>{t.viewBtn}</button>
                        <button onClick={() => openEdit(risk)} style={btnSmall}>{t.editBtn}</button>
                        <button onClick={() => handleDeleteOne(risk)} style={{ ...btnSmall, border: BTN_DESTRUCTIVE_OUTLINE.border, color: BTN_DESTRUCTIVE_OUTLINE.color }}>{t.deleteBtn}</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        content={confirmState?.content}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel}
        confirmColor={confirmState?.confirmColor}
        secondaryAction={confirmState?.secondaryAction}
      />
    </div>
  );
}
